import { formatDateTime, formatRelative } from '@/lib/format';

interface RelativeTimeProps {
  /** ISO-8601 Timestamp aus dem Backend. */
  value: string | null | undefined;
  /**
   * Anzeige-Variante:
   *  - 'relative-with-tooltip' (default): "vor 3 Tagen", title=absolut
   *  - 'absolute-with-tooltip': "30.04.2026 14:00", title=relativ
   */
  variant?: 'relative-with-tooltip' | 'absolute-with-tooltip';
  /** Fallback wenn value null/undefined. */
  fallback?: string;
  className?: string;
}

/**
 * Atomare Komponente: relative Zeitangabe + absolute Zeit als Tooltip.
 *
 * Nutzt `formatRelative` und `formatDateTime` aus `@/lib/format` (Locale de-AT,
 * TZ Europe/Vienna). Die <time>-Element-Semantik ermöglicht es Browser-Plugins
 * und Screen-Readern, die ISO-Variante zu lesen.
 */
export function RelativeTime({
  value,
  variant = 'relative-with-tooltip',
  fallback = '–',
  className,
}: RelativeTimeProps): JSX.Element {
  if (!value) {
    return <span className={className}>{fallback}</span>;
  }

  const relative = formatRelative(value);
  const absolute = formatDateTime(value);

  if (variant === 'absolute-with-tooltip') {
    return (
      <time
        dateTime={value}
        title={relative}
        className={className}
        aria-label={`${absolute} (${relative})`}
      >
        {absolute}
      </time>
    );
  }

  return (
    <time
      dateTime={value}
      title={absolute}
      className={className}
      aria-label={`${relative} (${absolute})`}
    >
      {relative}
    </time>
  );
}
